/**
 * Consistent loading affordances used across the site.
 *
 * - `<Spinner/>` — small inline spinner for buttons / inline status.
 * - `<LoadingBlock/>` — centered block spinner for page-level / panel-level
 *   loading states. Replaces the old bare `<p>Loading…</p>` placeholders.
 *
 * Both render an accessible status region so screen readers announce the
 * loading state without us repeating the `aria-live` / `role="status"` plumbing
 * everywhere. Visually-hidden text ("Loading…") is provided so the spinner
 * still has a name when the visible label is omitted.
 */

interface SpinnerProps {
  /** Visible label rendered next to the spinner. Hidden visually if `srOnly`. */
  label?: string;
  /** Bootstrap text-* color modifier (without the `text-` prefix). */
  variant?: 'primary' | 'secondary' | 'light' | 'dark';
  /** When true, the label is only exposed to assistive tech. */
  srOnly?: boolean;
  /** Use the smaller `spinner-border-sm` variant. Defaults to true for inline use. */
  small?: boolean;
  className?: string;
}

export function Spinner({
  label = 'Loading…',
  variant = 'primary',
  srOnly = false,
  small = true,
  className,
}: SpinnerProps) {
  const sizeClass = small ? 'spinner-border-sm' : '';
  const wrapperClass = ['d-inline-flex', 'align-items-center', 'gap-2', className].filter(Boolean).join(' ');
  return (
    <span className={wrapperClass} role="status" aria-live="polite">
      <span
        className={`spinner-border ${sizeClass} text-${variant}`.trim()}
        aria-hidden="true"
      />
      {srOnly ? (
        <span className="visually-hidden">{label}</span>
      ) : (
        <span className="small text-muted">{label}</span>
      )}
    </span>
  );
}

interface LoadingBlockProps {
  label?: string;
  /** Minimum vertical space for the block. Useful to avoid layout jumps. */
  minHeight?: number | string;
  variant?: 'primary' | 'secondary' | 'light' | 'dark';
  className?: string;
}

export function LoadingBlock({
  label = 'Loading…',
  minHeight = '12rem',
  variant = 'primary',
  className,
}: LoadingBlockProps) {
  const cls = ['d-flex', 'justify-content-center', 'align-items-center', 'py-4', className].filter(Boolean).join(' ');
  return (
    <div className={cls} style={{ minHeight }} role="status" aria-live="polite">
      <div className="text-center">
        <div className={`spinner-border text-${variant}`} aria-hidden="true" />
        <div className="mt-2 text-muted small">{label}</div>
      </div>
    </div>
  );
}

export default Spinner;
